import type { ReaderCharacter } from "../api";

/**
 * CharacterFace is a reader's picture of a character (ADR-0077, requirement
 * 0034) — the published edition's portrait, round, beside their name.
 *
 * One component for the three places a reader meets a character — the book
 * page's cast, character arcs, and the cards a pressed line offers in the
 * chat — because the rule is the same in all three and would otherwise drift:
 * **the layout does not move when there is no face.** A book whose cast has
 * no portraits, or one published before they were drawn, must not read as a
 * page with holes in it, so the fallback is an initial in the same circle and
 * not a blank.
 */
export default function CharacterFace({
  character,
  size = 40,
}: {
  character: Pick<ReaderCharacter, "name" | "portrait">;
  size?: number;
}) {
  const box = { width: size, height: size };
  if (character.portrait) {
    return (
      <img
        src={character.portrait.url}
        alt=""
        style={box}
        width={character.portrait.width}
        height={character.portrait.height}
        loading="lazy"
        data-testid="character-face"
        className="shrink-0 rounded-full bg-sunk object-cover"
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      style={{ ...box, fontSize: Math.round(size * 0.4) }}
      data-testid="character-no-face"
      className="grid shrink-0 place-items-center rounded-full bg-sage-soft font-medium text-sage-deep"
    >
      {initialOf(character.name)}
    </span>
  );
}

/**
 * initialOf is the first letter of the name, by rune and upper-cased.
 *
 * `Array.from`, not `name[0]`: a Persian name is two bytes a letter and an
 * emoji or a surrogate pair would be cut in half by an index (CLAUDE.md,
 * "count runes, not bytes"). A leading English article is skipped, so "the
 * Underground Man" is U, not t (requirement 0134) — unless it is the whole
 * name.
 */
export function initialOf(name: string): string {
  let t = name.trim();
  const article = /^(the|an|a)\s+/i.exec(t);
  if (article && article[0].length < t.length) t = t.slice(article[0].length);
  const first = Array.from(t)[0];
  return first === undefined ? "?" : (Array.from(first.toUpperCase())[0] ?? first);
}
